<template>
  <section id="servicios" class="servicios-section">
    <div class="container">
      <span class="label-chip"><i class="fas fa-bolt"></i> Lo que ofrecemos</span>
      <h2 class="section-title">Nuestros Servicios</h2>
      <p class="section-desc">Todo lo que necesitas en un solo lugar, sin complicaciones.</p>

      <div class="service-tabs">
        <button
          v-for="(service, idx) in services"
          :key="service.title"
          class="service-tab"
          :class="{ active: idx === currentPage }"
          @click="goToPage(idx)"
        >
          {{ service.tabLabel }}
        </button>
      </div>

      <transition name="fade-up" mode="out-in">
        <div class="service-panel" :key="currentPage">
          <div class="service-icon-wrap">
            <i :class="services[currentPage].icon"></i>
          </div>
          <div class="service-body">
            <h3>{{ services[currentPage].title }}</h3>
            <p>{{ services[currentPage].description }}</p>
            <div class="service-pills">
              <span
                v-for="pill in services[currentPage].pills"
                :key="pill"
                class="service-pill"
              >{{ pill }}</span>
            </div>
            <a href="#contacto" class="btn btn-ghost-light btn-sm" @click.prevent="scrollToContact">
              Saber más <i class="fas fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </transition>
    </div>
  </section>
</template>

<script>
import { trackEvent } from '../utils/analytics';

export default {
  name: 'ServiciosGallery',
  data() {
    return {
      currentPage: 0,
      rotateInterval: null,
      services: [
        {
          tabLabel: 'Mercado Libre',
          icon: 'fas fa-store',
          title: 'Agencia de Mercado Libre',
          description: 'Somos punto oficial de entrega y devolución de Mercado Libre. Recoge tus paquetes cómodamente o realiza tus devoluciones sin complicaciones, rápido y seguro.',
          pills: ['Recolección de paquetes', 'Devoluciones', 'Punto oficial']
        },
        {
          tabLabel: 'Impresiones',
          icon: 'fas fa-print',
          title: 'Impresión de Documentos',
          description: 'Impresión en blanco y negro o a color para documentos, copias. Calidad garantizada a precios accesibles, entrega en minutos.',
          pills: ['Color', 'Blanco y negro', 'Carta y oficio']
        },
        {
          tabLabel: 'Pago de Servicios',
          icon: 'fas fa-receipt',
          title: 'Pago de Servicios',
          description: 'Paga tu luz, teléfono e internet y más, sin filas ni complicaciones. Servicio rápido y seguro directamente en nuestra tienda.',
          pills: ['CFE', 'Teléfono', 'Internet']
        },
        {
          tabLabel: 'Papelería',
          icon: 'fas fa-book-open',
          title: 'Papelería Especializada',
          description: 'Engargolado, enmicado, forrado de libros, libretas  y servicios de papelería profesional para escuelas y empresas. Terminados de calidad a tu alcance.',
          pills: ['Engargolado', 'Enmicado', 'Forrado de libros']
        }
      ]
    };
  },
  methods: {
    goToPage(idx) {
      trackEvent('servicio_tab_click', { service: this.services[idx].tabLabel });
      this.currentPage = idx;
      this.resetRotation();
    },
    startRotation() {
      this.rotateInterval = setInterval(() => {
        this.currentPage = (this.currentPage + 1) % this.services.length;
      }, 6000);
    },
    resetRotation() {
      if (this.rotateInterval) clearInterval(this.rotateInterval);
      this.startRotation();
    },
    scrollToContact() {
      trackEvent('servicio_saber_mas', { service: this.services[this.currentPage].tabLabel });
      const el = document.getElementById('contacto');
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    }
  },
  mounted() {
    this.startRotation();
  },
  beforeUnmount() {
    if (this.rotateInterval) clearInterval(this.rotateInterval);
  }
};
</script>

<style scoped>
.servicios-section {
  background: #003b4c;
  padding: 96px 0;
}

.label-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255,255,255,0.5);
  background: rgba(255,255,255,0.08);
  border: 1px solid rgba(255,255,255,0.14);
  padding: 5px 14px;
  border-radius: 999px;
  margin-bottom: 14px;
}
.label-chip i { color: #f6dfa4; }

.section-title {
  font-family: 'Lora', serif;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: 600;
  color: #ffffff;
  line-height: 1.2;
  margin-bottom: 10px;
  margin-top: 0;
}

.section-desc {
  font-size: 1rem;
  color: rgba(255,255,255,0.5);
  margin-bottom: 40px;
}

.service-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 48px;
  flex-wrap: wrap;
}

.service-tab {
  padding: 9px 20px;
  border-radius: 999px;
  border: 1.5px solid rgba(255,255,255,0.2);
  background: rgba(255,255,255,0.06);
  color: rgba(255,255,255,0.65);
  font-family: marine, 'Inter', 'Poppins', sans-serif;
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s ease;
}
.service-tab:hover {
  background: rgba(255,255,255,0.12);
  color: #fff;
}
.service-tab.active {
  background: #ed8f7b;
  border-color: #ed8f7b;
  color: #fff;
}

.service-panel {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 40px;
  align-items: start;
}

.service-icon-wrap {
  width: 88px;
  height: 88px;
  border-radius: 14px;
  background: rgba(246,223,164,0.12);
  border: 1.5px solid rgba(246,223,164,0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.2rem;
  color: #f6dfa4;
  flex-shrink: 0;
}

.service-body h3 {
  font-family: 'Lora', serif;
  font-size: clamp(1.3rem, 2.5vw, 1.8rem);
  color: #ffffff;
  margin: 0 0 12px;
  font-weight: 600;
  line-height: 1.2;
}

.service-body > p {
  color: rgba(255,255,255,0.68);
  font-size: 0.97rem;
  line-height: 1.7;
  max-width: 560px;
  margin: 0 0 16px;
}

.service-pills {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 24px;
}

.service-pill {
  padding: 5px 14px;
  border-radius: 999px;
  background: rgba(240,194,178,0.15);
  border: 1.5px solid rgba(240,194,178,0.25);
  color: #f0c2b2;
  font-size: 0.78rem;
  font-weight: 600;
}

.btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 11px 26px;
  border-radius: 999px;
  font-family: marine, 'Inter', 'Poppins', sans-serif;
  font-size: 0.88rem;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.18s ease;
  border: 1.5px solid transparent;
}
.btn-sm { padding: 8px 18px; font-size: 0.8rem; }
.btn-ghost-light {
  background: rgba(255,255,255,0.1);
  color: #fff;
  border-color: rgba(255,255,255,0.25);
}
.btn-ghost-light:hover {
  background: rgba(255,255,255,0.2);
  border-color: rgba(255,255,255,0.5);
}

/* Transition */
.fade-up-enter-active,
.fade-up-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.fade-up-enter-from { opacity: 0; transform: translateY(10px); }
.fade-up-leave-to  { opacity: 0; transform: translateY(-6px); }

@media (max-width: 640px) {
  .service-panel { grid-template-columns: 1fr; }
  .service-icon-wrap { width: 64px; height: 64px; font-size: 1.6rem; }
}
</style>
